/**
 * «Ринок» — УЕБ (/market/ueex) — РЕДИЗАЙН (VITE_REDESIGN).
 *
 * Прод без прапорця бачить незмінений UeexPageLegacy у page.tsx: маршрут не
 * гейтований жодним VITE_FEATURE_*, тож у прод-збірці сторінка присутня.
 *
 * Що виправлено проти старої версії:
 *  · 55 хардкод-кольорів і 30 Tailwind-класів палітри (text-gray-500,
 *    bg-gray-50, bg-red-50, bg-green-100, text-yellow-800 …) — на токени;
 *    ті класи майже не мали dark:-варіантів, тож у темній темі банер Solar GO,
 *    порожні стани й статус-пілюлі лишались світлими;
 *  · сторонні акценти #0066FF / #8B5CF6 / #F59E0B / #10B981 / #EF4444 у
 *    графіках — на догму (§3), червоного немає ніде;
 *  · власна права колонка-сайдбар із секціями → SegmentedTabs у топбарі (§6);
 *  · 14 Remix-іконок → Lucide, 13 font-mono → font-display (§0);
 *  · recharts тепер бере кольори через getComputedStyle (§8) — раніше вони були
 *    літералами й не реагували на зміну теми, а сітка була завжди #F3F4F6.
 *
 * Запити, розрахунки, фільтри й склад таблиць не змінені.
 */
import i18n from '@/i18n';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import DashboardLayout from '@/components/feature/DashboardLayout';
import { api } from '@/services/api';
import { useTheme } from '@/hooks/useTheme';
import { useUeexUi } from '@/hooks/useUeexUi';
import {
  Database, ExternalLink, Handshake, Info, Layers, LineChart as LineChartIcon,
  ShoppingCart, Sun, TriangleAlert, Zap, type LucideIcon,
} from 'lucide-react';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, ComposedChart,
} from 'recharts';

// ─────────────────────────── типи (дзеркало page.tsx) ───────────────────────────
interface TimeSeriesPoint {
  date: string;
  base_price?: number | null; block_price?: number | null;
  base_volume_mwh?: number | null; block_volume_mwh?: number | null;
  peak_price?: number | null; offpeak_price?: number | null;
  [key: string]: any;
}
interface ElectricityTimeseriesResponse {
  windowDays: number; rows: number;
  stats: Record<string, { goods: string; totalMwh: number; totalUah: number; positions: number; wavgPrice: number | null }>;
  points: TimeSeriesPoint[];
}
interface SolarGoPoint {
  date: string; sellPositions: number; closedWithBuyer: number;
  offeredMwh: number | null; realizedMwh: number | null; weightedAvgPrice: number | null;
  avgAskPrice: number | null; minAskPrice: number | null; maxAskPrice: number | null;
}
interface SolarGoResponse { points: SolarGoPoint[]; windowDays: number }
interface Auction {
  aucNum: string; aucDate: string; aucTypeName: string; aucState: string;
  positionsCount: number; closedCount: number;
  totalMwh: number | null; totalUah: number | null; wavgPrice: number | null; goods: string[];
}

const fmtNum = (v: any, digits = 0): string => {
  if (v === null || v === undefined) return '—';
  const n = Number(v);
  return Number.isNaN(n) ? '—' : n.toLocaleString('uk-UA', { minimumFractionDigits: digits, maximumFractionDigits: digits });
};
const fmtDate = (d: string): string => {
  if (!d) return '';
  const [y, m, day] = d.split('-');
  return `${day}.${m}.${y}`;
};

/**
 * Recharts пише кольори у SVG-атрибути, а вони не резолвлять var(--…).
 * Догма серій: BASE → --series-3 (основна ціна, як РДН деінде), Блочна →
 * --warn, PEAK → --ok, OFFPEAK → нейтральний сірий. Червоного немає.
 */
function useUeexColors() {
  const { isDark } = useTheme();
  return useMemo(() => {
    const css = getComputedStyle(document.documentElement);
    const v = (n: string, fb: string) => css.getPropertyValue(n).trim() || fb;
    return {
      base: v('--series-3', isDark ? '#C56467' : '#A6575B'),
      block: v('--warn', isDark ? '#E0A24A' : '#C77A1B'),
      peak: v('--ok', isDark ? '#4CAF6A' : '#2E7D46'),
      offpeak: v('--series-2', isDark ? '#C9CCCE' : '#5E656D'),
      grid: v('--line', isDark ? 'rgba(255,255,255,0.08)' : '#E4E5E7'),
      tick: v('--text-dim', isDark ? '#7E868E' : '#9AA0A6'),
      text: v('--text', isDark ? '#F2F2F2' : '#28323C'),
      surface: v('--surface-solid', isDark ? '#2F3A45' : '#FFFFFF'),
    };
  }, [isDark]);
}

// ─────────────────────────── спільні примітиви ───────────────────────────
const th: any = {
  textAlign: 'left', padding: '8px 10px', fontSize: 10.5, fontWeight: 700,
  color: 'var(--text-muted)', background: 'var(--surface-2)',
  borderBottom: '1px solid var(--line)', whiteSpace: 'nowrap',
};
const td: any = {
  textAlign: 'left', padding: '8px 10px', fontSize: 12.5,
  color: 'var(--text)', borderBottom: '1px solid var(--line)',
};

function Note({ tone, Icon, children }: { tone: 'info' | 'warn' | 'err'; Icon: LucideIcon; children: any }) {
  const c = tone === 'err' ? 'var(--err)' : tone === 'warn' ? 'var(--warn)' : 'var(--text-muted)';
  return (
    <div className="text-[12px] flex items-start gap-2 py-2 pl-3" style={{ borderLeft: `3px solid ${c}`, color: c }}>
      <Icon size={14} strokeWidth={2} style={{ marginTop: 1, flexShrink: 0 }} />
      <div className="min-w-0">{children}</div>
    </div>
  );
}

function TileHead({ Icon, title, subtitle, right }: { Icon: LucideIcon; title: string; subtitle?: string; right?: any }) {
  return (
    <div className="flex items-start justify-between flex-wrap gap-2 mb-3">
      <div className="flex items-start gap-2 min-w-0">
        <Icon size={15} strokeWidth={1.9} style={{ color: 'var(--text-muted)', marginTop: 2, flexShrink: 0 }} />
        <div className="min-w-0">
          <h3 className="font-display text-[13.5px] font-semibold" style={{ color: 'var(--text)' }}>{title}</h3>
          {subtitle && <div className="text-[11.5px] mt-0.5" style={{ color: 'var(--text-muted)' }}>{subtitle}</div>}
        </div>
      </div>
      {right}
    </div>
  );
}

/** Показник: колір лише на іконці, число нейтральне й без font-mono (§5). */
function Kpi({ Icon, color, title, value, unit, subtitle }: {
  Icon: LucideIcon; color: string; title: string; value: string; unit?: string; subtitle?: string;
}) {
  return (
    <div className="tile p-4 flex flex-col" style={{ minHeight: 108 }}>
      <div className="flex items-center gap-2 mb-1">
        <Icon size={16} strokeWidth={2} style={{ color, flexShrink: 0 }} />
        <span className="text-[12.5px] font-semibold truncate" style={{ color: 'var(--text)' }} title={title}>{title}</span>
      </div>
      <div className="flex items-baseline gap-1 mt-2">
        <span className="font-display text-[20px] font-normal leading-none" style={{ color: 'var(--text)' }}>{value}</span>
        {unit && <span className="text-[10px]" style={{ color: 'var(--text-muted)' }}>{unit}</span>}
      </div>
      {subtitle && <div className="text-[10.5px] mt-1.5" style={{ color: 'var(--text-dim)' }}>{subtitle}</div>}
    </div>
  );
}

function ChartLegend({ items }: { items: { label: string; color: string; dashed?: boolean }[] }) {
  return (
    <div className="flex items-center justify-center gap-x-5 gap-y-1.5 flex-wrap pt-3">
      {items.map((it) => (
        <span key={it.label} className="inline-flex items-center gap-1.5" style={{ fontSize: 12, color: 'var(--text-muted)' }}>
          <span style={{ width: 16, height: 0, display: 'inline-block', borderTop: `3px ${it.dashed ? 'dashed' : 'solid'} ${it.color}` }} />
          {it.label}
        </span>
      ))}
    </div>
  );
}

function LoadingBox({ t }: { t: (k: string) => string }) {
  return <div className="tile p-8 text-center text-[12.5px]" style={{ color: 'var(--text-muted)' }}>{t('ueex_loading')}</div>;
}
function ErrorBox({ msg }: { msg: string }) {
  return <div className="tile p-3"><Note tone="err" Icon={TriangleAlert}>{msg}</Note></div>;
}

// ─────────────────────────── Електроенергія ───────────────────────────
function ElectricPowerSection({ days, t }: { days: number; t: (k: string) => string }) {
  const c = useUeexColors();
  const [data, setData] = useState<ElectricityTimeseriesResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    api.get<ElectricityTimeseriesResponse>(`/market/ueex/electricity-timeseries?days=${days}`)
      .then((r) => { if (!cancelled) setData(r); })
      .catch((e) => { if (!cancelled) setError(e?.message ?? 'load failed'); });
    return () => { cancelled = true; };
  }, [days]);

  if (error) return <ErrorBox msg={error} />;
  if (!data) return <LoadingBox t={t} />;

  const baseStats = data.stats?.base;
  const blockStats = data.stats?.block;
  const peakStats = data.stats?.peak;
  const premium = baseStats?.wavgPrice && blockStats?.wavgPrice
    ? ((baseStats.wavgPrice - blockStats.wavgPrice) / blockStats.wavgPrice) * 100
    : null;

  const axis = { tick: { fill: c.tick, fontSize: 10 }, axisLine: false as const, tickLine: false as const };
  const tip = {
    contentStyle: { background: c.surface, border: `1px solid ${c.grid}`, borderRadius: 10, fontSize: 12, color: c.text },
    labelStyle: { color: c.text, fontWeight: 700 },
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <Kpi Icon={Zap} color="var(--series-3)" title={t('ueex_kpi_base_wavg')}
          value={fmtNum(baseStats?.wavgPrice, 0)} unit={t('ak_ueex_uah_mwh')}
          subtitle={i18n.t('ak_ueex_mwh_deals_2', { mwh: fmtNum(baseStats?.totalMwh, 0), n: baseStats?.positions ?? 0 })} />
        <Kpi Icon={Layers} color="var(--warn)" title={t('ueex_kpi_block_wavg')}
          value={fmtNum(blockStats?.wavgPrice, 0)} unit={t('ak_ueex_uah_mwh')}
          subtitle={i18n.t('ak_ueex_mwh_deals_2', { mwh: fmtNum(blockStats?.totalMwh, 0), n: blockStats?.positions ?? 0 })} />
        <Kpi Icon={Sun} color="var(--ok)" title={t('ueex_kpi_peak_wavg')}
          value={peakStats?.wavgPrice ? fmtNum(peakStats.wavgPrice, 0) : '—'} unit={t('ak_ueex_uah_mwh')}
          subtitle={peakStats ? i18n.t('ak_ueex_mwh', { mwh: fmtNum(peakStats.totalMwh, 0) }) : t('ueex_no_data')} />
        {/* Премія — знак несе догму: вище → --ok, нижче → --warn. */}
        <Kpi Icon={LineChartIcon}
          color={premium == null ? 'var(--text-muted)' : premium >= 0 ? 'var(--ok)' : 'var(--warn)'}
          title={t('ueex_kpi_premium')}
          value={premium == null ? '—' : `${premium.toFixed(1)}%`}
          subtitle={t('ak_ueex_base_vs_block')} />
      </div>

      <div className="tile p-4 sm:p-5">
        <TileHead Icon={LineChartIcon} title={t('ueex_chart_auctions_title')} subtitle={t('ueex_chart_auctions_subtitle')} />
        <ResponsiveContainer width="100%" height={360}>
          <LineChart data={data.points} margin={{ top: 10, right: 14, bottom: 5, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={c.grid} vertical={false} />
            <XAxis dataKey="date" tickFormatter={fmtDate} {...axis} />
            <YAxis {...axis} width={64}
              label={{ value: t('ak_ueex_uah_excl_vat'), angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: c.tick, textAnchor: 'middle' } }} />
            <Tooltip {...tip} formatter={(v: any) => i18n.t('ak_ueex_uah', { n: fmtNum(v, 0) })} labelFormatter={fmtDate} />
            <Line type="monotone" dataKey="base_price" stroke={c.base} name="BASE (24/7)" strokeWidth={2.4} dot={{ r: 2.5 }} connectNulls isAnimationActive={false} />
            <Line type="monotone" dataKey="block_price" stroke={c.block} name={t('ak_ueex_block')} strokeWidth={2.4} dot={{ r: 2.5 }} connectNulls isAnimationActive={false} />
            <Line type="monotone" dataKey="peak_price" stroke={c.peak} name="PEAK" strokeWidth={2} dot={{ r: 2.5 }} connectNulls isAnimationActive={false} />
            <Line type="monotone" dataKey="offpeak_price" stroke={c.offpeak} name="OFFPEAK" strokeWidth={2} dot={{ r: 2.5 }} connectNulls isAnimationActive={false} />
          </LineChart>
        </ResponsiveContainer>
        <ChartLegend items={[
          { label: 'BASE (24/7)', color: c.base },
          { label: t('ak_ueex_block'), color: c.block },
          { label: 'PEAK', color: c.peak },
          { label: 'OFFPEAK', color: c.offpeak },
        ]} />
        <div className="text-[10.5px] text-right mt-2" style={{ color: 'var(--text-dim)' }}>
          {t('ueex_source_note')}: epbets.ueex.com.ua
        </div>
      </div>

      <div className="tile p-4 sm:p-5">
        <TileHead Icon={Database} title={t('ueex_chart_volume_title')} />
        <ResponsiveContainer width="100%" height={220}>
          <BarChart data={data.points} margin={{ top: 5, right: 14, bottom: 5, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={c.grid} vertical={false} />
            <XAxis dataKey="date" tickFormatter={fmtDate} {...axis} />
            <YAxis {...axis} width={64}
              label={{ value: t('ak_ueex_mwh_3'), angle: -90, position: 'insideLeft', style: { fontSize: 11, fill: c.tick, textAnchor: 'middle' } }} />
            <Tooltip {...tip} formatter={(v: any) => i18n.t('ak_ueex_mwh_2', { n: fmtNum(v, 0) })} labelFormatter={fmtDate} />
            <Bar dataKey="base_volume_mwh" stackId="a" fill={c.base} fillOpacity={0.55} name="BASE" isAnimationActive={false} />
            <Bar dataKey="block_volume_mwh" stackId="a" fill={c.block} fillOpacity={0.55} name={t('ak_ueex_block')} isAnimationActive={false} />
          </BarChart>
        </ResponsiveContainer>
        <ChartLegend items={[{ label: 'BASE', color: c.base }, { label: t('ak_ueex_block'), color: c.block }]} />
      </div>
    </div>
  );
}

// ─────────────────────────── Двосторонні договори ───────────────────────────
function BilateralContractsSection({ days, t }: { days: number; t: (k: string) => string }) {
  const [auctions, setAuctions] = useState<Auction[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    api.get<{ auctions: Auction[] }>(`/market/ueex/recent-auctions?days=${days}&limit=200`)
      .then((r) => {
        if (cancelled) return;
        // Filter to electricity-related auction types
        const electricity = r.auctions.filter((a) =>
          a.goods.some((g) =>
            ['Базове навантаження', 'Блочна позиція', 'Пікове навантаження', 'Позапікове навантаження',
             'Пікове навантаження неробочого дня', 'Навантаження індивідуального профілю'].includes(g)
          )
        );
        setAuctions(electricity);
      })
      .catch((e) => { if (!cancelled) setError(e?.message ?? 'load failed'); });
    return () => { cancelled = true; };
  }, [days]);

  if (error) return <ErrorBox msg={error} />;

  return (
    <div className="tile p-4 sm:p-5">
      <TileHead Icon={Handshake} title={t('ueex_bilateral_title')}
        subtitle={`${t('ueex_bilateral_subtitle')} · ${auctions.length} ${t('ueex_records')}`} />
      <div className="overflow-x-auto">
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={th}>{t('ueex_auc_num')}</th>
            <th style={th}>{t('ueex_auc_date')}</th>
            <th style={th}>{t('ueex_auc_type')}</th>
            <th style={{ ...th, textAlign: 'right' }}>{t('ueex_auc_closed')}/{t('ueex_auc_positions')}</th>
            <th style={{ ...th, textAlign: 'right' }}>{t('ak_ueex_mwh_3')}</th>
            <th style={{ ...th, textAlign: 'right' }}>{t('ueex_wavg_price')}</th>
            <th style={{ ...th, textAlign: 'right' }}>{t('ueex_revenue')}, {t('ak_ueex_uah_2')}</th>
          </tr></thead>
          <tbody>
            {auctions.length === 0 && (
              <tr><td colSpan={7} className="py-6 text-center text-[12.5px]" style={{ color: 'var(--text-muted)' }}>{t('ueex_no_data')}</td></tr>
            )}
            {auctions.map((a) => (
              <tr key={a.aucNum}>
                <td style={{ ...td, fontSize: 11.5, color: 'var(--text-muted)' }}>{a.aucNum}</td>
                <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDate(a.aucDate)}</td>
                <td style={{ ...td, fontSize: 12 }}>{a.aucTypeName}</td>
                <td className="font-display" style={{ ...td, textAlign: 'right' }}>{a.closedCount}/{a.positionsCount}</td>
                <td className="font-display" style={{ ...td, textAlign: 'right' }}>{fmtNum(a.totalMwh, 0)}</td>
                <td className="font-display" style={{ ...td, textAlign: 'right' }}>{fmtNum(a.wavgPrice, 0)}</td>
                <td className="font-display" style={{ ...td, textAlign: 'right' }}>{fmtNum(a.totalUah, 0)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────── Solar GO ───────────────────────────
function SolarGoSection({ days, t }: { days: number; t: (k: string) => string }) {
  const c = useUeexColors();
  const [data, setData] = useState<SolarGoResponse | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    api.get<SolarGoResponse>(`/market/ueex/solar-go-timeseries?days=${days}`)
      .then((r) => { if (!cancelled) setData(r); })
      .catch((e) => { if (!cancelled) setError(e?.message ?? 'load failed'); });
    return () => { cancelled = true; };
  }, [days]);

  if (error) return <ErrorBox msg={error} />;
  if (!data) return <LoadingBox t={t} />;

  const totalSell = data.points.reduce((s, p) => s + p.sellPositions, 0);
  const totalClosed = data.points.reduce((s, p) => s + p.closedWithBuyer, 0);
  const totalOffered = data.points.reduce((s, p) => s + (p.offeredMwh ?? 0), 0);
  const asks = data.points.map((p) => p.avgAskPrice).filter((v) => v !== null) as number[];
  const avgAsk = asks.length ? asks.reduce((a, b) => a + b, 0) / asks.length : null;

  const axis = { tick: { fill: c.tick, fontSize: 10 }, axisLine: false as const, tickLine: false as const };

  return (
    <div className="flex flex-col gap-3">
      <div className="tile p-4 sm:p-5">
        <Note tone="warn" Icon={Info}>
          <strong>{t('ueex_solar_banner_title')}.</strong> {t('ueex_solar_banner_text')}
        </Note>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-4 gap-3">
        <Kpi Icon={Sun} color="var(--series-3)" title={t('ueex_solar_avg_ask')} value={avgAsk ? fmtNum(avgAsk, 2) : '—'} unit={t('ak_ueex_uah_mwh')} />
        <Kpi Icon={ShoppingCart} color="var(--text-muted)" title={t('ueex_solar_sell_positions')} value={fmtNum(totalSell, 0)} unit={t('ak_ueex_lots')} />
        <Kpi Icon={Handshake} color="var(--ok)" title={t('ueex_solar_closed_deals')} value={fmtNum(totalClosed, 0)} unit={t('ak_ueex_deals')} />
        <Kpi Icon={Database} color="var(--text-muted)" title={t('ueex_solar_offered_volume')} value={fmtNum(totalOffered, 0)} unit={t('ak_ueex_mwh_3')} />
      </div>

      <div className="tile p-4 sm:p-5">
        <TileHead Icon={Sun} title={t('ueex_solar_chart_title')} />
        <ResponsiveContainer width="100%" height={320}>
          <ComposedChart data={data.points} margin={{ top: 8, right: 14, bottom: 5, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={c.grid} vertical={false} />
            <XAxis dataKey="date" tickFormatter={fmtDate} {...axis} />
            <YAxis yAxisId="left" {...axis} width={64} />
            <YAxis yAxisId="right" orientation="right" {...axis} width={56} />
            <Tooltip labelFormatter={fmtDate}
              contentStyle={{ background: c.surface, border: `1px solid ${c.grid}`, borderRadius: 10, fontSize: 12, color: c.text }}
              labelStyle={{ color: c.text, fontWeight: 700 }} />
            {/* Обсяг — нейтральний фон під ціновою смугою. */}
            <Bar yAxisId="right" dataKey="offeredMwh" fill={c.offpeak} fillOpacity={0.28} name={t('ak_ueex_offered_mwh')} isAnimationActive={false} />
            <Line yAxisId="left" dataKey="avgAskPrice" stroke={c.base} name="Avg Ask" strokeWidth={2.4} dot={false} isAnimationActive={false} />
            <Line yAxisId="left" dataKey="minAskPrice" stroke={c.peak} name="Min Ask" strokeWidth={1.4} strokeDasharray="4 4" dot={false} isAnimationActive={false} />
            <Line yAxisId="left" dataKey="maxAskPrice" stroke={c.block} name="Max Ask" strokeWidth={1.4} strokeDasharray="4 4" dot={false} isAnimationActive={false} />
          </ComposedChart>
        </ResponsiveContainer>
        <ChartLegend items={[
          { label: 'Avg Ask', color: c.base },
          { label: 'Min Ask', color: c.peak, dashed: true },
          { label: 'Max Ask', color: c.block, dashed: true },
          { label: t('ak_ueex_offered_mwh'), color: c.offpeak },
        ]} />
      </div>
    </div>
  );
}

// ─────────────────────────── Усі ВДЕ ГП ───────────────────────────
function RenewableGoSection({ t }: { t: (k: string) => string }) {
  return (
    <div className="tile p-4 sm:p-5">
      <TileHead Icon={Sun} title={t('ueex_renewable_title')} subtitle={t('ueex_renewable_subtitle')} />
      <div className="py-8 text-center text-[12.5px]" style={{ color: 'var(--text-muted)' }}>
        {t('ueex_renewable_placeholder')}
        <div className="text-[11px] mt-1" style={{ color: 'var(--text-dim)' }}>{t('ueex_renewable_data_source')}</div>
      </div>
    </div>
  );
}

// ─────────────────────────── Аукціони ───────────────────────────
function AuctionsSection({ days, t }: { days: number; t: (k: string) => string }) {
  const [auctions, setAuctions] = useState<Auction[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<string>('all');

  useEffect(() => {
    let cancelled = false;
    setError(null);
    api.get<{ auctions: Auction[] }>(`/market/ueex/recent-auctions?days=${days}&limit=200`)
      .then((r) => { if (!cancelled) setAuctions(r.auctions); })
      .catch((e) => { if (!cancelled) setError(e?.message ?? 'load failed'); });
    return () => { cancelled = true; };
  }, [days]);

  if (error) return <ErrorBox msg={error} />;

  const allTypes = Array.from(new Set(auctions.map((a) => a.aucTypeName))).filter(Boolean);
  const filtered = typeFilter === 'all' ? auctions : auctions.filter((a) => a.aucTypeName === typeFilter);

  return (
    <div className="tile p-4 sm:p-5">
      <TileHead Icon={Layers} title={`${t('ueex_all_auctions_title')} (${filtered.length})`}
        right={
          <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}
            className="outline-none cursor-pointer"
            style={{ height: 36, borderRadius: 10, background: 'var(--surface-2)',
                     border: '1px solid var(--line)', color: 'var(--text)', padding: '0 10px', fontSize: 12.5, maxWidth: 260 }}>
            <option value="all">{t('ueex_all_types')}</option>
            {allTypes.map((tp) => <option key={tp} value={tp}>{tp}</option>)}
          </select>
        } />
      <div className="overflow-x-auto">
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead><tr>
            <th style={th}>{t('ueex_auc_num')}</th>
            <th style={th}>{t('ueex_auc_date')}</th>
            <th style={th}>{t('ueex_auc_type')}</th>
            <th style={th}>{t('ueex_auc_state')}</th>
            <th style={{ ...th, textAlign: 'right' }}>{t('ueex_auc_positions')}</th>
            <th style={{ ...th, textAlign: 'right' }}>{t('ueex_auc_closed')}</th>
            <th style={{ ...th, textAlign: 'right' }}>{t('ak_ueex_mwh_3')}</th>
            <th style={{ ...th, textAlign: 'right' }}>{t('ueex_wavg_price')}</th>
          </tr></thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={8} className="py-6 text-center text-[12.5px]" style={{ color: 'var(--text-muted)' }}>{t('ueex_no_data')}</td></tr>
            )}
            {filtered.map((a) => {
              // «Закрито» — завершений стан, нейтральний; решта — активний торг.
              const stCol = a.aucState === 'Закрито' ? 'var(--text-dim)' : 'var(--ok)';
              return (
                <tr key={a.aucNum}>
                  <td style={{ ...td, fontSize: 11.5, color: 'var(--text-muted)' }}>{a.aucNum}</td>
                  <td style={{ ...td, whiteSpace: 'nowrap' }}>{fmtDate(a.aucDate)}</td>
                  <td style={{ ...td, fontSize: 12 }}>{a.aucTypeName}</td>
                  <td style={td}>
                    <span className="text-[10px] px-1.5 py-0.5 whitespace-nowrap"
                      style={{ borderRadius: 6, color: stCol, border: `1px solid ${stCol}`,
                               background: `color-mix(in srgb, ${stCol} 12%, transparent)` }}>
                      {a.aucState}
                    </span>
                  </td>
                  <td className="font-display" style={{ ...td, textAlign: 'right' }}>{a.positionsCount}</td>
                  <td className="font-display" style={{ ...td, textAlign: 'right' }}>{a.closedCount}</td>
                  <td className="font-display" style={{ ...td, textAlign: 'right' }}>{fmtNum(a.totalMwh, 0)}</td>
                  <td className="font-display" style={{ ...td, textAlign: 'right' }}>{fmtNum(a.wavgPrice, 0)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─────────────────────────── каркас ───────────────────────────
export default function UeexRedesign() {
  const { t } = useTranslation();
  const { section, days } = useUeexUi();

  return (
    <DashboardLayout breadcrumb={[t('nav_an_market')]}>
      <div className="min-h-screen -m-4 md:-m-6" style={{ background: 'var(--bg)' }}>
        <div className="px-4 md:px-6 pt-5 pb-10" style={{ fontFamily: 'Urbanist, Inter, sans-serif' }}>

          {/* Заголовок у тілі не дублюємо — назву розділу показує топбар.
              Лишається лише посилання на джерело. */}
          <div className="mb-3 flex justify-end">
            <a href="https://www.ueex.com.ua/exchange-quotations/electric-power/"
              target="_blank" rel="noopener noreferrer"
              className="text-[12px] inline-flex items-center gap-1.5 hover:underline whitespace-nowrap"
              style={{ color: 'var(--text-muted)' }}>
              {t('ueex_external_link')}<ExternalLink size={12} strokeWidth={2} />
            </a>
          </div>

          {section === 'electric_power' && <ElectricPowerSection days={days} t={t} />}
          {section === 'bilateral_contracts' && <BilateralContractsSection days={days} t={t} />}
          {section === 'solar_go' && <SolarGoSection days={days} t={t} />}
          {section === 'renewable_go' && <RenewableGoSection t={t} />}
          {section === 'auctions' && <AuctionsSection days={days} t={t} />}
        </div>
      </div>
    </DashboardLayout>
  );
}
